import { useNavigate } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { IntuiButton } from '@/components/intueri/IntuiButton'

interface ErrorPageProps {
  /** Omitted for faults with no HTTP status at all -- see `ClientErrorPage`. */
  code?: number
  title: string
  description: string
  actionLabel: string
  onAction: () => void
  actionDisabled?: boolean
  secondaryAction?: { label: string; onClick: () => void }
}

function ErrorPage({ code, title, description, actionLabel, onAction, actionDisabled, secondaryAction }: ErrorPageProps) {
  return (
    <div className="flex h-screen w-full flex-col items-center justify-center bg-white dark:bg-dark-card">
      {/* Logo */}
      <img
        src="/images/intuai-logo.svg"
        alt="Intuigence"
        width={160}
        height={24}
        draggable={false}
        className="dark:hidden"
      />
      <img
        src="/images/intuai-logo-dark.svg"
        alt="Intuigence"
        width={160}
        height={24}
        draggable={false}
        className="hidden dark:block"
      />

      {/* Content */}
      <div className="mt-9 flex w-[420px] flex-col items-center gap-9 p-6 text-center">
        {/* Header */}
        <div className="flex flex-col gap-2">
          <h1 className="text-xl font-medium text-foreground">
            {code === undefined ? title : `${code} - ${title}`}
          </h1>
          <p className="text-base leading-6 text-muted-foreground">
            {description}
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col items-center gap-4">
          <IntuiButton size="lg" onClick={onAction} disabled={actionDisabled}>
            {actionLabel}
          </IntuiButton>
          {secondaryAction && (
            <IntuiButton
              variant="link"
              onClick={secondaryAction.onClick}
              rightIcon={<ArrowRight />}
              className="text-muted-foreground"
            >
              {secondaryAction.label}
            </IntuiButton>
          )}
        </div>
      </div>
    </div>
  )
}

// --- 4xx Client Errors ---

export function BadRequestPage() {
  return (
    <ErrorPage
      code={400}
      title="Bad Request"
      description="Something's off with that request. There may be a typo or formatting issue in the URL. Double-check it and try again."
      actionLabel="Try Again"
      onAction={() => window.location.reload()}
    />
  )
}

export function UnauthorizedPage() {
  return (
    <ErrorPage
      code={401}
      title="Unauthorized Access"
      description="You'll need to sign in first. This page is only available to logged-in users. Sign in to continue."
      actionLabel="Sign in to continue"
      onAction={() => { window.location.href = '/' }}
    />
  )
}

export function ForbiddenPage() {
  const navigate = useNavigate()
  return (
    <ErrorPage
      code={403}
      title="Forbidden Access"
      description="You don't have access to this. Your account doesn't have permission to view this page. If you think this is a mistake, contact your admin."
      actionLabel="Go Back"
      onAction={() => navigate(-1)}
      secondaryAction={{
        label: 'Contact Admin',
        onClick: () => window.open('mailto:support@intuigence.ai'),
      }}
    />
  )
}

export function NotFoundPage() {
  return (
    <ErrorPage
      code={404}
      title="Not Found"
      description="The page you're looking for doesn't exist. The page may have moved, been deleted, or the URL might be wrong."
      actionLabel="Go Home"
      onAction={() => { window.location.href = '/intelligent-board' }}
    />
  )
}

export function RequestTimeoutPage() {
  return (
    <ErrorPage
      code={408}
      title="Request Timeout"
      description="That took too long. Your connection timed out before the page could load. Check your internet and try again."
      actionLabel="Try Again"
      onAction={() => window.location.reload()}
    />
  )
}

export function TooManyRequestsPage() {
  return (
    <ErrorPage
      code={429}
      title="Too Many Requests"
      description="You've made too many requests in a short time. Wait a moment and try again."
      actionLabel="Try again in 60 seconds"
      actionDisabled
      onAction={() => window.location.reload()}
    />
  )
}

// --- 5xx Server Errors ---

export function InternalServerErrorPage() {
  return (
    <ErrorPage
      code={500}
      title="Internal Server Error"
      description="Something went wrong on our end. It's not you, our server hit an unexpected issue. We're looking into it."
      actionLabel="Refresh Page"
      onAction={() => window.location.reload()}
    />
  )
}

/**
 * For a fault that happened in THIS browser -- a render error, or a route
 * loader that rejected. No HTTP status, because no server necessarily answered.
 *
 * `InternalServerErrorPage` is the wrong page for that and actively misleads:
 * it prints "500" and says the failure is "on our end". Two incidents have
 * already been chased into the backend on the strength of it (see
 * `workflowGraph.ts` -- "every API call in those traces returned 200" -- and
 * the UI-01 file-attach crash). Keep that page for a real upstream 500.
 */
export function ClientErrorPage() {
  return (
    <ErrorPage
      title="Something went wrong"
      description="This page ran into an unexpected problem and couldn't finish loading. Refreshing usually fixes it. If it keeps happening, let us know."
      actionLabel="Refresh Page"
      onAction={() => window.location.reload()}
    />
  )
}

export function NotImplementedPage() {
  const navigate = useNavigate()
  return (
    <ErrorPage
      code={501}
      title="Not Implemented"
      description="This isn't supported yet. The feature you're trying to use isn't available right now."
      actionLabel="Go Back"
      onAction={() => navigate(-1)}
    />
  )
}

export function BadGatewayPage() {
  return (
    <ErrorPage
      code={502}
      title="Bad Gateway"
      description="We're having trouble connecting. Our servers got an unexpected response. This is usually temporary."
      actionLabel="Refresh Page"
      onAction={() => window.location.reload()}
    />
  )
}

export function ServiceUnavailablePage() {
  return (
    <ErrorPage
      code={503}
      title="Service Unavailable"
      description="We'll be back soon. The service is temporarily down, likely for maintenance or high traffic. Try again in a few minutes."
      actionLabel="Refresh Page"
      onAction={() => window.location.reload()}
    />
  )
}

export function GatewayTimeoutPage() {
  return (
    <ErrorPage
      code={504}
      title="Gateway Timeout"
      description="The server took too long to respond. Something upstream didn't respond in time. This is usually temporary."
      actionLabel="Try Again"
      onAction={() => window.location.reload()}
    />
  )
}

export default ErrorPage
